import { useState, useEffect } from "react";
import axios from "axios";
import { Coins, FloppyDisk, ArrowClockwise, Check } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TokenManager() {
  const [cfg, setCfg] = useState(null);
  const [earnRate, setEarnRate] = useState(10);
  const [redeemRate, setRedeemRate] = useState(100);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const fetchCfg = async () => {
    try {
      const res = await axios.get(`${API}/admin/tokens/config`, { withCredentials: true });
      setCfg(res.data);
      setEarnRate(res.data.earn_per_tree);
      setRedeemRate(res.data.redeem_tokens_per_ton);
    } catch (e) {
      setError("Failed to load config");
    }
  };

  useEffect(() => { fetchCfg(); }, []);

  const save = async () => {
    setError(""); setSaving(true); setSaved(false);
    try {
      const res = await axios.put(`${API}/admin/tokens/config`,
        { earn_per_tree: Number(earnRate), redeem_tokens_per_ton: Number(redeemRate) },
        { withCredentials: true },
      );
      setCfg(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <p className="text-stone-400 text-sm">Loading…</p>;

  return (
    <div className="bg-stone-900 border border-stone-800 rounded-2xl p-6 max-w-2xl" data-testid="token-manager">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-full bg-emerald-600/20 flex items-center justify-center">
          <Coins size={20} weight="fill" className="text-emerald-400" />
        </div>
        <div className="flex-1">
          <h2 className="font-heading text-lg font-semibold text-white">TREE Token Economy</h2>
          <p className="text-xs text-stone-400">Configure how users earn & redeem Conexus Foundation TREE tokens.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-5">
        <Field
          label="Earn per tree planted"
          hint="TREE awarded when a user creates a verified social post."
          value={earnRate} onChange={setEarnRate} suffix="TREE / tree"
          testId="token-earn-rate"
        />
        <Field
          label="Redemption rate"
          hint="TREE tokens required to mint 1 tCO₂e offset certificate."
          value={redeemRate} onChange={setRedeemRate} suffix="TREE / tCO₂e"
          testId="token-redeem-rate"
        />
      </div>

      <div className="bg-emerald-600/10 border border-emerald-600/30 rounded-xl p-3 mb-4 text-xs text-emerald-200">
        Example: A user plants <strong>10 trees</strong> → earns <strong>{earnRate * 10} TREE</strong> →
        can redeem <strong>{(earnRate * 10 / redeemRate).toFixed(2)} tCO₂e</strong> of offset certificate.
      </div>

      {error && <p className="text-xs text-red-400 mb-3">{error}</p>}

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-stone-700 text-white text-sm font-medium px-5 py-2.5 rounded-full"
          data-testid="token-save"
        >
          <FloppyDisk size={14} weight="fill" /> {saving ? "Saving…" : "Save Configuration"}
        </button>
        <button
          onClick={fetchCfg}
          className="text-stone-400 hover:text-white text-xs"
          data-testid="token-reload"
        >
          <ArrowClockwise size={14} className="inline" /> Reload
        </button>
        {saved && (
          <span className="text-emerald-400 text-xs inline-flex items-center gap-1" data-testid="token-saved">
            <Check size={14} weight="bold" /> Saved
          </span>
        )}
      </div>
    </div>
  );
}

function Field({ label, hint, value, onChange, suffix, testId }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-stone-400 mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="number" min="0" step="1"
          value={value}
          onChange={(e) => onChange(Number(e.target.value) || 0)}
          className="flex-1 bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500"
          data-testid={testId}
        />
        <span className="text-[11px] text-stone-500">{suffix}</span>
      </div>
      <p className="text-[10px] text-stone-500 mt-1">{hint}</p>
    </div>
  );
}
